/**
 * DecisionControl — renders ONE registry decision (pick-one chips, toggling
 * multi chips, or palette swatches). Fully registry-driven: adding an option
 * or a decision to the spec registry changes the wizard review step and the
 * Settings tab with no edit here. Locked options render disabled with a
 * "coming soon" tag.
 */
import {
  DECISIONS,
  type AnyDecision,
  type DecisionId,
  type GameSettings,
} from '@djgg/creations';

export interface DecisionControlProps {
  id: DecisionId;
  /** The value to render as selected. For `multi` decisions this is the
   *  selected SET (readonly id array). */
  value: GameSettings[DecisionId] | undefined;
  onAnswer: (id: DecisionId, value: GameSettings[DecisionId]) => void;
  disabled?: boolean;
  disabledHint?: string;
  error?: string;
  /** The option id the concept analyzer proposed for this decision (wizard
   *  review only — SettingsPanel never passes it). Tags that option
   *  "suggested for this concept" regardless of what's currently selected,
   *  so the creator can see the recommendation even after overriding it. */
  suggestedOptionId?: string;
  /** The analyzer's one-line rationale, shown beside the decision when set.
   *  Callers decide WHEN it's worth showing (the wizard shows it only for a
   *  landscape orientation proposal — the wide choice has to justify itself). */
  suggestedReason?: string;
}

/** A small sticker-style shape glyph — phone-tall / monitor-wide / square —
 *  for the `orientation` decision's options, scaled in a shared box so the
 *  three read as a direct size/shape comparison. Ink-outlined, no fill
 *  color beyond the panel paper, matching the sticker-sheet chip chrome. */
function OrientationGlyph({ optionId }: { optionId: string }) {
  const shape = { fill: 'var(--bg-panel)', stroke: 'var(--ink)', strokeWidth: 2 };
  return (
    <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true" style={{ display: 'block' }}>
      {optionId === 'portrait' && <rect x="10" y="3" width="12" height="26" rx="3" {...shape} />}
      {optionId === 'landscape' && <rect x="3" y="9" width="26" height="14" rx="2" {...shape} />}
      {optionId === 'square' && <rect x="7" y="7" width="18" height="18" rx="2" {...shape} />}
    </svg>
  );
}

export function DecisionControl({
  id,
  value,
  onAnswer,
  disabled,
  disabledHint,
  error,
  suggestedOptionId,
  suggestedReason,
}: DecisionControlProps) {
  const decision: AnyDecision = DECISIONS[id];
  return (
    <div className="field" data-testid={`decision-${id}`}>
      <label className="field__label">{decision.label}</label>
      {decision.help && (
        <p className="muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
          {decision.help}
        </p>
      )}

      {/* `orientation` gets a shape-preview grid (phone/monitor/square glyphs)
       *  instead of plain text chips — on a phone the world shape decides
       *  whether the game is even playable, so it earns a picture, not just a
       *  label. Still the SAME DecisionControl, driven by the decision id —
       *  no forked control component. */}
      {decision.kind === 'choice' && id === 'orientation' && (
        <>
          <div className="orientation-shapes" data-testid="orientation-shapes">
            {decision.options.map((o) => {
              const active = value === o.id;
              const suggested = suggestedOptionId === o.id;
              return (
                <button
                  type="button"
                  key={o.id}
                  className={`orientation-shape ${active ? 'orientation-shape--active' : ''}`}
                  aria-pressed={active}
                  title={o.spec}
                  disabled={disabled || o.locked}
                  data-testid={`option-${id}-${o.id}`}
                  onClick={() => onAnswer(id, o.id as GameSettings[DecisionId])}
                >
                  <OrientationGlyph optionId={o.id} />
                  <span className="orientation-shape__label">
                    {o.label}
                    {o.locked ? ' · coming soon' : ''}
                  </span>
                  {suggested && (
                    <span className="orientation-shape__tag" data-testid={`suggested-${id}-${o.id}`}>
                      Suggested for this concept
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {suggestedReason && (
            <p className="muted orientation-reason" data-testid="orientation-suggested-reason">
              {suggestedReason}
            </p>
          )}
        </>
      )}

      {decision.kind === 'choice' && id !== 'orientation' && (
        <div className="chips">
          {decision.options.map((o) => (
            <button
              type="button"
              key={o.id}
              className={`chip ${value === o.id ? 'chip--active' : ''}`}
              aria-pressed={value === o.id}
              title={o.blurb ?? ''}
              disabled={disabled || o.locked}
              data-testid={`option-${id}-${o.id}`}
              onClick={() => onAnswer(id, o.id as GameSettings[DecisionId])}
            >
              {o.label}
              {o.locked ? ' · coming soon' : ''}
            </button>
          ))}
        </div>
      )}

      {decision.kind === 'multi' && (
        <div className="chips">
          {decision.options.map((o) => {
            const selected = ((value as readonly string[] | undefined) ?? []).includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                className={`chip ${selected ? 'chip--active' : ''}`}
                title={o.blurb ?? ''}
                aria-pressed={selected}
                disabled={disabled || o.locked}
                data-testid={`option-${id}-${o.id}`}
                onClick={() => {
                  const current = (value as readonly string[] | undefined) ?? [];
                  const next = selected
                    ? current.filter((entry) => entry !== o.id)
                    : [...current, o.id];
                  onAnswer(id, next as GameSettings[DecisionId]);
                }}
              >
                {o.label}
                {o.locked ? ' · coming soon' : ''}
              </button>
            );
          })}
        </div>
      )}

      {decision.kind === 'palette' && (
        <div className="chips">
          {decision.presets.map((p) => (
            <button
              type="button"
              key={p.id}
              className={`chip ${value === p.id ? 'chip--active' : ''}`}
              aria-pressed={value === p.id}
              disabled={disabled}
              data-testid={`option-${id}-${p.id}`}
              onClick={() => onAnswer(id, p.id as GameSettings[DecisionId])}
            >
              <span style={{ display: 'inline-flex', gap: 3, marginRight: 6, verticalAlign: 'middle' }}>
                {p.colors.map((c) => (
                  <span
                    key={c}
                    style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }}
                  />
                ))}
              </span>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {disabled && disabledHint && (
        <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
          {disabledHint}
        </p>
      )}
      {error && <div className="field__error">{error}</div>}
    </div>
  );
}
